import React from 'react';
import { isAnswered } from '../utils/oasisPdf';

const DASH = { value: '-', label: 'Not assessed / no information' };

const Choice = ({ it, value, onChange }) => (
  <div className="space-y-1">
    {[...(it.options || []), ...(it.dash ? [DASH] : [])].map(o => (
      <label key={o.value} className={`flex items-start gap-2 px-2 py-1 rounded cursor-pointer text-sm ${value === o.value ? 'bg-amber-50 ring-1 ring-[#d4af37]' : 'hover:bg-gray-50'}`}>
        <input type="radio" name={it.code} checked={value === o.value} readOnly onClick={() => onChange(value === o.value ? '' : o.value)} onKeyDown={(e) => { if (e.key === ' ' && value === o.value) { e.preventDefault(); onChange(''); } }} className="mt-1" data-testid={`oasis-${it.code}-${o.value}`} />
        <span><strong className="mr-1">{o.value}.</strong>{o.label}</span>
      </label>
    ))}
  </div>
);

const Multi = ({ it, value = [], onChange }) => {
  const list = Array.isArray(value) ? value : [];
  const toggle = (v) => onChange(list.includes(v) ? list.filter(x => x !== v) : v === '-' ? ['-'] : [...list.filter(x => x !== '-'), v]);
  return (
    <div className="space-y-1">
      {[...(it.options || []), ...(it.dash ? [DASH] : [])].map(o => (
        <label key={o.value} className={`flex items-start gap-2 px-2 py-1 rounded cursor-pointer text-sm ${list.includes(o.value) ? 'bg-amber-50 ring-1 ring-[#d4af37]' : 'hover:bg-gray-50'}`}>
          <input type="checkbox" checked={list.includes(o.value)} onChange={() => toggle(o.value)} className="mt-1" data-testid={`oasis-${it.code}-${o.value}`} />
          <span><strong className="mr-1">{o.value}.</strong>{o.label}</span>
        </label>
      ))}
    </div>
  );
};

const Grid = ({ it, value, onChange }) => {
  const v = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const cols = it.columns?.length ? it.columns : [{ code: '', label: 'Code' }];
  const set = (k, x) => onChange({ ...v, [k]: x });
  return (
    <div className="overflow-x-auto">
      {(it.options || []).length > 0 && (
        <details className="mb-2 text-xs text-gray-600"><summary className="cursor-pointer font-medium">Coding scale</summary>
          <ul className="mt-1 space-y-0.5">{it.options.map(o => <li key={o.value}><strong>{o.value}</strong> {o.label}</li>)}</ul>
        </details>
      )}
      <table className="w-full text-sm border">
        <thead className="bg-gray-50"><tr><th className="text-left p-2 border">Item</th>{cols.map(c => <th key={c.code} className="p-2 border text-left whitespace-pre-line">{c.label}</th>)}</tr></thead>
        <tbody>
          {(it.rows || []).map(r => (
            <tr key={r.code}>
              <td className="p-2 border"><strong>{r.code}.</strong> {r.label}</td>
              {cols.map(c => {
                const k = c.code ? `${r.code}|${c.code}` : r.code;
                const opts = r.options?.length ? r.options : it.options || [];
                return (
                  <td key={k} className="p-1 border min-w-[9rem]">
                    {opts.length ? (
                      <select value={v[k] || ''} onChange={(e) => set(k, e.target.value)} className="w-full border rounded px-1 py-1 text-sm" data-testid={`oasis-${it.code}-${k}`}>
                        <option value="">Select</option>
                        {[...opts, ...(it.dash ? [DASH] : [])].map(o => <option key={o.value} value={o.value}>{o.value} - {o.label.slice(0, 60)}</option>)}
                      </select>
                    ) : (
                      <input value={v[k] || ''} onChange={(e) => set(k, e.target.value)} className="w-full border rounded px-2 py-1 text-sm" data-testid={`oasis-${it.code}-${k}`} />
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export { isAnswered };

export const OasisItem = ({ it, value, onChange, locked }) => (
  <fieldset disabled={locked} className={`border rounded-lg p-4 bg-white ${isAnswered(it, value) ? 'border-green-200' : 'border-gray-200'}`} id={`oasis-item-${it.code}`} data-testid={`oasis-item-${it.code}`}>
    <h4 className="font-semibold text-gray-900">{it.code}. {it.title}</h4>
    {it.instruction && <p className="text-sm text-gray-600 whitespace-pre-line mt-1">{it.instruction}</p>}
    <div className="mt-3">
      {it.type === 'single' && <Choice it={it} value={value} onChange={onChange} />}
      {it.type === 'multi' && <Multi it={it} value={value} onChange={onChange} />}
      {it.type === 'grid' && <Grid it={it} value={value} onChange={onChange} />}
      {it.type === 'date' && (
        <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="MM/DD/YYYY" className="border rounded px-3 py-2 text-sm w-48" data-testid={`oasis-${it.code}-input`} />
      )}
      {['text', 'number', 'diagnosis'].includes(it.type) && (
        <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={it.type === 'diagnosis' ? 3 : 1}
          placeholder={it.type === 'diagnosis' ? 'ICD-10-CM code and description' : ''} className="w-full border rounded px-3 py-2 text-sm" data-testid={`oasis-${it.code}-input`} />
      )}
    </div>
    {it.skip && <p className="text-xs text-blue-700 mt-2" data-testid={`oasis-${it.code}-skip`}>Skip pattern: {it.skip}</p>}
  </fieldset>
);
